import { useState, useEffect } from 'react';
import { getKidsChildren, getKidsMealCounts, getTeamData } from '../../utils/api';

// Kids Overview — the staff view's landing page. Stat cards + program
// breakdown of registered kids + quick links + Kat-facing meal totals.

const PROGRAMS = ['Elementary', '5-6', '3-4'];
const DAY_ORDER = ['Thursday', 'Friday', 'Saturday', 'Sunday', 'Monday'];
const MEAL_ORDER = ['Breakfast', 'Lunch', 'Dinner'];

export default function KidsOverview({ team, setCurrentView, showToast }) {
  const [children, setChildren] = useState([]);
  const [groups, setGroups] = useState([]);
  const [meals, setMeals] = useState(null);
  const [slCount, setSlCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getKidsChildren().catch(() => null),
      getKidsMealCounts().catch(() => null),
      team?.id ? getTeamData(team.id).catch(() => null) : Promise.resolve(null),
    ]).then(([kids, mealCounts, teamData]) => {
      if (cancelled) return;
      if (kids) { setChildren(kids.children || []); setGroups(kids.groups || []); }
      if (mealCounts) setMeals(mealCounts);
      if (teamData) setSlCount((teamData.members || []).length);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [team?.id]);

  const active = children.filter(c => c.status !== 'Cancelled');
  const allergyCount = active.filter(c => (c.displayAllergies || '').trim() !== '').length;
  const fmt = n => loading ? '—' : (n === null ? '—' : n.toLocaleString());

  const mealRows = meals
    ? Object.values(meals).sort((a, b) => {
        const da = DAY_ORDER.indexOf(a.day); const db = DAY_ORDER.indexOf(b.day);
        if (da !== db) return (da === -1 ? 99 : da) - (db === -1 ? 99 : db);
        return MEAL_ORDER.indexOf(a.mealType) - MEAL_ORDER.indexOf(b.mealType);
      })
    : [];

  const go = sub => setCurrentView(`tl-${team.id}-${sub}`);

  return (
    <div className="page-content">
      <div className="page-title-row">
        <div>
          <div className="page-title">Kids Overview</div>
          <div className="page-sub">The Belonging Co Kids Conference · 2026</div>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Servant Leaders</div>
          <div className="stat-value">{fmt(slCount)}</div>
          <div className="stat-sub">on the Kids roster</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Kids Registered</div>
          <div className="stat-value">{fmt(active.length)}</div>
          <div className="stat-sub">{loading ? '' : `${active.filter(c => c.isNew).length} new in the last 24h`}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Small Groups</div>
          <div className="stat-value">{fmt(groups.length)}</div>
          <div className="stat-sub">across 3 programs</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Allergies</div>
          <div className="stat-value">{fmt(allergyCount)}</div>
          <div className="stat-sub">kids with an allergy on file</div>
        </div>
      </div>

      {!loading && active.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div className="section-header">Program Breakdown</div>
          <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex' }}>
              {PROGRAMS.map((p, i) => (
                <div key={p} style={{ flex: 1, padding: '16px 20px', borderRight: i < PROGRAMS.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>{p}</div>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 26, fontWeight: 800, color: 'var(--black)', lineHeight: 1 }}>
                    {active.filter(c => c.program === p).length}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="two-col-equal">
        <div>
          <div className="section-header">Quick Actions</div>
          <div className="panel" style={{ display: 'flex', flexDirection: 'column' }}>
            {[
              { label: 'People.', sub: 'people' },
              { label: 'Small Groups.', sub: 'groups' },
              { label: 'Allergies.', sub: 'allergies' },
              { label: 'Rotations.', sub: 'rotations' },
              { label: 'Daily Details.', sub: 'daily' },
            ].map(item => (
              <div key={item.sub} className="quick-link" style={{ flex: 1 }} onClick={() => go(item.sub)}>
                <span className="quick-link-name">{item.label}</span>
                <span style={{ color: 'var(--muted)' }}>→</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="section-header">Kids Meal Totals (union rule — internal only)</div>
          <div className="panel">
            {mealRows.length === 0 ? (
              <div style={{ padding: '20px 24px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
                No meal counts yet — they appear once people are rostered into blocks.
              </div>
            ) : (
              <table className="data-table">
                <thead><tr><th>Day</th><th>Meal</th><th>People</th></tr></thead>
                <tbody>
                  {mealRows.map((r, i) => (
                    <tr key={i} style={{ cursor: 'default' }}>
                      <td className="td-name">{r.day}</td><td>{r.mealType}</td><td>{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
